import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Table, DatePicker, Spin, Empty, Select, notification } from "antd";
import { Column } from "@ant-design/plots";
import dayjs from "dayjs";
import PageHeader from "../../../components/Common/PageHeader/PageHeader";
import { getToken } from "../../../utils/utility";
import { hourlyStatisticsForTablesRequest } from "../../../services/organizationsStatisticsService";
import "./index.css";

const modeOptions = [
  { label: "Всички", value: "all" },
  { label: "mymenudelivery", value: "mymenudelivery" },
  { label: "mymenuordering", value: "mymenuordering" },
  { label: "mymenupreview", value: "mymenupreview" },
];

const StatisticsTime = () => {
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedOrgs, setSelectedOrgs] = useState([]);
  const [selectedMode, setSelectedMode] = useState("all");

  const fetchData = useCallback(async (date) => {
    setLoading(true);
    try {
      const token = getToken().get("idToken");
      const formattedDate = date.format("YYYY-MM-DD");
      const response = await hourlyStatisticsForTablesRequest(
        token,
        formattedDate
      );
      if (response[0] === 200) {
        setData(response[1]);
      } else {
        notification["error"]({ message: "Грешка при зареждане на данните" });
      }
    } catch {
      notification["error"]({ message: "Грешка" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(selectedDate);
  }, [selectedDate, fetchData]);

  const onDateChange = (date) => {
    if (date) {
      setSelectedDate(date);
    }
  };

  const organizationOptions = useMemo(() => {
    return data.map((org) => ({
      label: org.organizationName,
      value: org.organizationId,
    }));
  }, [data]);

  const hours = Array.from({ length: 24 }, (_, i) => ({
    hourKey: `${String(i).padStart(2, "0")}:00-${String((i + 1) % 24).padStart(2, "0")}:00`,
    hourShort: `${i}-${(i + 1) % 24}`,
    index: i,
  }));

  const filteredData = useMemo(() => {
    let result = data;
    if (selectedOrgs.length > 0) {
      result = result.filter((org) =>
        selectedOrgs.includes(org.organizationId)
      );
    }
    if (selectedMode !== "all") {
      result = result
        .map((org) => ({
          ...org,
          tables: org.tables.filter(
            (tbl) => tbl.tableName?.toLowerCase() === selectedMode
          ),
        }))
        .filter((org) => org.tables.length > 0);
    }
    return result;
  }, [data, selectedOrgs, selectedMode]);

  const allRows = [];
  filteredData.forEach((org) => {
    org.tables.forEach((tbl) => {
      const row = {
        key: `${org.organizationId}-${tbl.tableId}`,
        organizationName: org.organizationName,
        tableName: tbl.tableName,
        total: 0,
      };
      tbl.hourlyVisits.forEach((hv) => {
        row[hv.hour] = hv.count;
        row.total += hv.count;
      });
      allRows.push(row);
    });
  });

  const tableRows = allRows
    .filter((r) => r.total > 0)
    .sort((a, b) => b.total - a.total);

  const totalRow = {
    key: "total-row",
    organizationName: "ОБЩО",
    tableName: "",
    total: 0,
  };
  hours.forEach((h) => {
    totalRow[h.hourKey] = tableRows.reduce(
      (sum, r) => sum + (r[h.hourKey] || 0),
      0
    );
    totalRow.total += totalRow[h.hourKey];
  });

  const nonZeroHours = hours.filter((h) => {
    return tableRows.some((r) => (r[h.hourKey] || 0) > 0);
  });

  const columns = [
    {
      title: "Заведение",
      dataIndex: "organizationName",
      fixed: "left",
      width: 180,
    },
    {
      title: "Маса (режим)",
      dataIndex: "tableName",
      fixed: "left",
      width: 150,
    },
    ...nonZeroHours.map((h) => ({
      title: h.hourShort,
      dataIndex: h.hourKey,
      align: "center",
      width: 55,
      render: (val) => (
        <span
          className={
            val > 0
              ? "statistics-time-cell-highlight"
              : "statistics-time-cell-zero"
          }
        >
          {val || 0}
        </span>
      ),
    })),
    {
      title: "Общо",
      dataIndex: "total",
      fixed: "right",
      align: "center",
      width: 65,
      render: (val) => <strong>{val || 0}</strong>,
    },
  ];

  const dataSourceWithTotal =
    tableRows.length > 0 ? [...tableRows, totalRow] : [];

  const chartData = useMemo(() => {
    const result = [];
    nonZeroHours.forEach((h) => {
      filteredData.forEach((org) => {
        org.tables.forEach((tbl) => {
          const bucket = tbl.hourlyVisits.find((hv) => hv.hour === h.hourKey);
          const count = bucket ? bucket.count : 0;
          if (count > 0) {
            result.push({
              hour: h.hourShort,
              type: `${org.organizationName} (${tbl.tableName})`,
              value: count,
            });
          }
        });
      });
    });
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredData, nonZeroHours]);

  const hasChartValues = chartData.some((item) => item.value > 0);

  const chartConfig = {
    data: chartData,
    isStack: true,
    xField: "hour",
    yField: "value",
    seriesField: "type",
    legend: {
      position: "top",
    },
    xAxis: {
      label: {
        autoRotate: true,
        autoHide: true,
      },
    },
    tooltip: {
      formatter: (datum) => {
        return { name: datum.type, value: datum.value };
      },
    },
    color: [
      "#3066BE", "#F46036", "#F0A500", "#2E933C",
      "#8B5CF6", "#EC4899", "#14B8A6", "#F59E0B",
      "#6366F1", "#EF4444", "#10B981", "#F97316",
    ],
  };

  return (
    <>
      <PageHeader text="Статистика по часове" />
      <div className="statistics-time-container">
        <div className="statistics-time-actions">
          <DatePicker
            value={selectedDate}
            onChange={onDateChange}
            format="DD.MM.YYYY"
            allowClear={false}
          />
          <Select
            mode="multiple"
            showSearch
            allowClear
            placeholder="Всички организации"
            className="statistics-time-org-select"
            value={selectedOrgs}
            onChange={setSelectedOrgs}
            options={organizationOptions}
            maxTagCount="responsive"
            filterOption={(input, option) =>
              (option?.label ?? "")
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          />
          <Select
            value={selectedMode}
            onChange={setSelectedMode}
            options={modeOptions}
            className="statistics-time-mode-select"
          />
        </div>

        <div className="statistics-time-chart-wrapper">
          <div className="statistics-time-chart-header">
            <h3>Сканирания по часове</h3>
            <span>
              {selectedDate.format("DD.MM.YYYY")} —{" "}
              {selectedMode === "all" ? "Всички режими" : selectedMode}
            </span>
          </div>
          <div className="statistics-time-chart">
            {loading ? (
              <Spin />
            ) : hasChartValues ? (
              <Column {...chartConfig} />
            ) : (
              <Empty description="Няма данни за графиката" />
            )}
          </div>
        </div>

        {loading ? (
          <div className="statistics-time-spinner">
            <Spin size="large" />
          </div>
        ) : dataSourceWithTotal.length === 0 ? (
          <Empty description="Няма данни за избрания ден" />
        ) : (
          <Table
            className="statistics-time-table"
            columns={columns}
            dataSource={dataSourceWithTotal}
            pagination={false}
            size="small"
            scroll={{ x: "max-content" }}
            bordered
            rowClassName={(record) =>
              record.key === "total-row" ? "statistics-time-total-row" : ""
            }
          />
        )}
      </div>
    </>
  );
};

export default StatisticsTime;
